import { Injectable } from '@angular/core';

import { LanguageService } from '@cloudia-shared/apis/generated/employee-language-api';
import { AuthService } from '@cloudia-shared/services/auth/auth.service';
import { SelectFetchService } from '@cloudia-shared/services/core/select-fetch.service';
import {
  DropdownModulesFixture,
  GenericObjectModel,
} from '@tcp/cloudia-utilities';
import { map, Observable, of, shareReplay, switchMap, take } from 'rxjs';
import { DropdownModules } from './dropdown.model';
import { HttpParams } from '@angular/common/http';

@Injectable({ providedIn: 'root' })
export class DropdownModulesService implements DropdownModulesFixture {
  constructor(
    private readonly selectFetchService: SelectFetchService,
    private readonly employeeLanguageApi: LanguageService,
    private readonly authService: AuthService
  ) {}

  getCostCenters(
    query: string,
    page: number,
    selected: any[],
    batchSize?: number
  ): Observable<GenericObjectModel[]> {
    return this.getDropdownValues(
      DropdownModules.COST_CENTERS,
      query,
      page,
      selected,
      batchSize
    );
  }

  getHubspotCountries(
    query: string,
    page: number,
    selected: any[],
    batchSize?: number
  ): Observable<GenericObjectModel[]> {
    return this.getDropdownValues(
      DropdownModules.HUBSPOT_COUNTRIES,
      query,
      page,
      selected,
      batchSize
    );
  }

  getHubspotStages(
    query: string,
    page: number,
    selected: any[],
    batchSize?: number
  ): Observable<GenericObjectModel[]> {
    return this.getDropdownValues(
      DropdownModules.HUBSPOT_STAGES,
      query,
      page,
      selected,
      batchSize
    );
  }

  getSOForProjectModuleTypes(
    query: string,
    page: number,
    selected: any[],
    batchSize?: number
  ): Observable<GenericObjectModel[]> {
    return this.getDropdownValues(
      DropdownModules.SO_REL_PROJECT_TYPES,
      query,
      page,
      selected,
      batchSize
    );
  }

  getCustomers(
    query: string,
    page: number,
    selected: any[],
    batchSize?: number
  ): Observable<GenericObjectModel[]> {
    return this.getDropdownValues(
      DropdownModules.CUSTOMERS,
      query,
      page,
      selected,
      batchSize
    );
  }

  getProjects(
    query: string,
    page: number,
    selected: any[],
    batchSize?: number
  ): Observable<GenericObjectModel[]> {
    return this.getDropdownValues(
      DropdownModules.PROJECTS,
      query,
      page,
      selected,
      batchSize
    );
  }

  getConsultants(
    query: string,
    page: number,
    selected: any[],
    batchSize?: number
  ): Observable<GenericObjectModel[]> {
    return this.getDropdownValues(
      DropdownModules.CONSULTANTS,
      query,
      page,
      selected,
      batchSize
    );
  }

  getHubspotDeals(
    query: string,
    page: number,
    selected: any[],
    batchSize?: number
  ): Observable<GenericObjectModel[]> {
    return this.getDropdownValues(
      DropdownModules.HUBSPOT_DEALS,
      query,
      page,
      selected,
      batchSize
    );
  }

  getUsers(
    query: string,
    page: number,
    selected: any[],
    batchSize?: number
  ): Observable<GenericObjectModel[]> {
    return this.getDropdownValues(
      DropdownModules.USERS,
      query,
      page,
      selected,
      batchSize
    );
  }

  getCertificates(
    query: string,
    page: number,
    selected: any[],
    batchSize?: number
  ): Observable<GenericObjectModel[]> {
    return this.getDropdownValues(
      DropdownModules.CERTIFICATES,
      query,
      page,
      selected,
      batchSize
    );
  }

  getTimezones(
    query: string,
    page: number,
    selected: any[],
    batchSize?: number
  ): Observable<GenericObjectModel[]> {
    return this.getDropdownValues(
      DropdownModules.TIMEZONES,
      query,
      page,
      selected,
      batchSize
    );
  }

  getDomains(
    query: string,
    page: number,
    selected: any[],
    batchSize?: number
  ): Observable<GenericObjectModel[]> {
    return this.getDropdownValues(
      DropdownModules.ALL_COMPETENCE_DOMAINS_FILTERED,
      query,
      page,
      selected,
      batchSize
    );
  }

  getGrades(
    query: string,
    page: number,
    selected: any[],
    batchSize?: number
  ): Observable<GenericObjectModel[]> {
    return this.getDropdownValues(
      DropdownModules.GRADES,
      query,
      page,
      selected,
      batchSize
    );
  }

  getIndustries(
    query: string,
    page: number,
    selected: any[],
    batchSize?: number
  ): Observable<GenericObjectModel[]> {
    return this.getDropdownValues(
      DropdownModules.SO_REL_PROJECT_CUSTOMERS_INDUSTRIES,
      query,
      page,
      selected,
      batchSize
    );
  }

  getLanguages(
    query: string,
    page: number,
    selected: any[],
    batchSize?: number
  ): Observable<GenericObjectModel[]> {
    return this.getDropdownValues(
      DropdownModules.SO_REL_EMPLOYEE_LANGUAGES,
      query,
      page,
      selected,
      batchSize
    );
  }

  getLanguagesExceptMine(
    query: string,
    page: number,
    selected: any[],
    batchSize?: number
  ) {
    return this.getDropdownValues(
      DropdownModules.SO_REL_EMPLOYEE_LANGUAGES,
      query,
      page,
      selected,
      batchSize
    ).pipe(
      switchMap((allLangsData) => {
        const authId = this.authService.getUserId();

        return this.employeeLanguageApi.listLanguages(authId, query, {}).pipe(
          switchMap((knownLangsData) => {
            const employeeLanguages = knownLangsData.items
              .filter((lang) => !selected.includes(lang.language))
              .map((lang) => lang.language);
            return of(
              allLangsData.filter(
                (language) => !employeeLanguages.includes(language.uniq)
              )
            );
          })
        );
      })
    );
  }

  getSkills(
    query: string,
    page: number,
    selected: any[],
    batchSize?: number
  ): Observable<GenericObjectModel[]> {
    return this.getDropdownValues(
      DropdownModules.SKILLS,
      query,
      page,
      selected,
      batchSize
    );
  }

  getOfferings(
    query: string,
    page: number,
    selected: any[],
    batchSize?: number
  ): Observable<GenericObjectModel[]> {
    return this.getDropdownValues(
      DropdownModules.OFFERINGS_ACTIVE,
      query,
      page,
      selected,
      batchSize
    );
  }

  convertModuleToMethod<DropdownModules>(module: DropdownModules) {
    return (
      query: string,
      page: number,
      selected: any[],
      batchSize?: number,
      tenant?: string
    ) =>
      this.getDropdownValues(
        module as any,
        query,
        page,
        selected,
        batchSize,
        tenant
      ).pipe(take(1), shareReplay(1));
  }

  private getDropdownValues(
    path: DropdownModules,
    query: string,
    page: number,
    selected: any[],
    batchSize?: number,
    tenant?: string
  ): Observable<GenericObjectModel[]> {
    if (!Array.isArray(selected)) {
      selected = [selected];
    }

    let params: HttpParams = new HttpParams();

    params = params
      .append(
        'query',
        this.selectFetchService.generateStaticObjectsQuery(query, path)
      )
      .append('selected', (selected || []).join(','))
      .append('page', page)
      .append('size', batchSize);

    if (tenant !== undefined) {
      params = params.append('tenant', tenant);
    }

    return this.selectFetchService
      .listObjects(params, path.includes('so:') ? 'static-objects' : path)
      .pipe(
        map((data) =>
          data.body.items.map((value) => value as GenericObjectModel)
        )
      );
  }
}
